import React, { useState, useEffect } from 'react';
import "bootstrap/dist/css/bootstrap.min.css"
import './App.css';
import {
  DateParts,
  formatArchiveDateLabel,
  getDaysInMonth,
  InvalidArchiveDateError,
  normalizeDomain,
  searchWaybackForDate,
  WaybackSearchResult,
  WaybackSnapshot,
  WaybackSearchProgress,
  WaybackServiceError,
} from './lib/wayback';

const SEARCH_PROGRESS_STEPS: { step: WaybackSearchProgress['step'], label: string }[] = [
  {
    step: 'prepare',
    label: 'Preparing request',
  },
  {
    step: 'availability',
    label: 'Checking availability',
  },
  {
    step: 'cdx-before',
    label: 'CDX before',
  },
  {
    step: 'cdx-after',
    label: 'CDX after',
  },
  {
    step: 'complete',
    label: 'Opening archive preview',
  },
]

function App() {
  const [isThemeDark, setIsThemeDark] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState('')
  const [companyQuery, setCompanyQuery] = useState('')
  const [selectedDomain, setSelectedDomain] = useState('')
  const [suggestions, setSuggestions] = useState<{ name: string, domain: string, logo?: string }[]>([])
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false)
  const [searchResult, setSearchResult] = useState<WaybackSearchResult | null>(null)
  const [activeSnapshot, setActiveSnapshot] = useState<WaybackSnapshot | null>(null)
  const [isTopNavHidden, setIsTopNavHidden] = useState(false)
  const [isIframeLoading, setIsIframeLoading] = useState(false)
  const [searchProgressState, setSearchProgressState] = useState<WaybackSearchProgress>({
    detail: 'Preparing archive search.',
    step: 'prepare',
  })
  const [searchingDomain, setSearchingDomain] = useState('')
  const [dateParts, setDateParts] = useState<DateParts>({ day: '21', month: '06', year: '2000' })

  const months = [
    { value: '01', label: 'Jan' },
    { value: '02', label: 'Feb' },
    { value: '03', label: 'Mar' },
    { value: '04', label: 'Apr' },
    { value: '05', label: 'May' },
    { value: '06', label: 'Jun' },
    { value: '07', label: 'Jul' },
    { value: '08', label: 'Aug' },
    { value: '09', label: 'Sep' },
    { value: '10', label: 'Oct' },
    { value: '11', label: 'Nov' },
    { value: '12', label: 'Dec' },
  ]
  const days = Array.from(
    { length: getDaysInMonth(Number(dateParts.year), Number(dateParts.month)) },
    (_, idx) => String(idx + 1).padStart(2, '0'),
  )
  const minYear = 1990
  const maxYear = new Date().getFullYear()
  const years = Array.from({ length: maxYear - minYear + 1 }, (_, idx) => String(minYear + idx))
  const hasPreview = Boolean(searchResult && activeSnapshot)
  const visibleProgressStep = searchProgressState.step === 'availability-hit' || searchProgressState.step === 'best-effort'
    ? 'complete'
    : searchProgressState.step === 'cdx-hit'
      ? 'cdx-after'
      : searchProgressState.step
  const searchStepIndex = Math.max(
    0,
    SEARCH_PROGRESS_STEPS.findIndex((step) => step.step === visibleProgressStep),
  )
  const searchStep = SEARCH_PROGRESS_STEPS[searchStepIndex] || SEARCH_PROGRESS_STEPS[0]
  const searchProgress = Math.round(((searchStepIndex + 1) / SEARCH_PROGRESS_STEPS.length) * 100)

  const clearPreview = () => {
    setSearchResult(null)
    setActiveSnapshot(null)
    setIsTopNavHidden(false)
    setIsIframeLoading(false)
  }

  async function getWebsite(event?: React.FormEvent, domainOverride?: string) {
    if (event) event.preventDefault()
    setError('')

    const domainToUse = normalizeDomain(
      domainOverride || selectedDomain || suggestions[0]?.domain || companyQuery,
    )

    if (!domainToUse || !domainToUse.includes('.')) {
      setIsSearching(false)
      setError('Pick a company or enter a full domain to continue.')
      return
    }

    setIsSearching(true)
    setSearchProgressState({
      detail: `Preparing ${domainToUse} for ${formatArchiveDateLabel(dateParts)}.`,
      step: 'prepare',
    })
    setSearchingDomain(domainToUse)
    clearPreview()

    try {
      const result = await searchWaybackForDate(domainToUse, dateParts, fetch, setSearchProgressState)

      if (!result.snapshot) {
        setIsSearching(false)
        setError(`No archived homepage was found near ${formatArchiveDateLabel(dateParts)} for ${domainToUse}. Try another date.`)
        return
      }

      setSearchProgressState({
        candidate: result.snapshot.candidate,
        detail: 'Opening archive preview.',
        step: 'complete',
      })
      setSearchResult(result)
      setIsIframeLoading(true)
      setActiveSnapshot(result.snapshot)
      setIsTopNavHidden(false)
      setIsSearching(false)
    } catch (lookupError) {
      setIsSearching(false)

      if (lookupError instanceof InvalidArchiveDateError || lookupError instanceof WaybackServiceError) {
        setError(lookupError.message)
        return
      }

      setError('An unexpected error occurred. Please try again.')
    }
  }
  const onDarkThemeButton = () => {
    //save the theme in local storage
    localStorage.setItem('isThemeDark', JSON.stringify(!isThemeDark));
    setIsThemeDark(!isThemeDark);
  }
  const companyChangeHandler = async (e: React.FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value
    setCompanyQuery(value);
    setSelectedDomain('');
    setError('');

    if (value.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    setIsLoadingCompanies(true);
    try {
      const response = await fetch(`https://autocomplete.clearbit.com/v1/companies/suggest?query=${encodeURIComponent(value)}`)

      if (!response.ok) {
        throw new Error(`Autocomplete request failed with status ${response.status}`)
      }

      const companies = await response.json()
      setSuggestions(companies || []);
    } catch (_) {
      setSuggestions([]);
    } finally {
      setIsLoadingCompanies(false);
    }
  }
  const onSuggestionClick = (domain: string) => {
    setSelectedDomain(normalizeDomain(domain));
    setError('');
    getWebsite(undefined, domain);
  }
  const dateChangeHandler = (key: 'day' | 'month' | 'year') => (e: React.FormEvent<HTMLSelectElement>) => {
    const value = e.currentTarget?.value
    if (!value) return
    setError('')
    setDateParts((prev) => ({ ...prev, [key]: value }));
  }

  useEffect(() => {
    const maxDay = getDaysInMonth(Number(dateParts.year), Number(dateParts.month))

    if (Number(dateParts.day) > maxDay) {
      setDateParts((prev) => ({ ...prev, day: String(maxDay).padStart(2, '0') }))
    }
  }, [dateParts.day, dateParts.month, dateParts.year]);

  useEffect(() => {
    const handleShow = (event: PageTransitionEvent) => {
      if (event.persisted) setIsSearching(false);
    };
    window.addEventListener('pageshow', handleShow);
    return () => window.removeEventListener('pageshow', handleShow);
  }, []);

  useEffect(() => {
    const storedTheme = localStorage.getItem('isThemeDark');
    if (storedTheme) {
      setIsThemeDark(JSON.parse(storedTheme));
    } else {
      localStorage.setItem('isThemeDark', JSON.stringify(isThemeDark));
    }
    setIsSearching(false);
  }, [isThemeDark]);


  return (
    <div
      className={`App ${isThemeDark ? 'text-light' : 'text-dark'} ${hasPreview ? 'App--preview' : 'App--landing'}`}
      style={{
        fontSmooth: "never",
        fontFamily: "Times New Roman",
        backgroundColor: `${isThemeDark ? 'black' : 'white'}`
      }}>
      {
        isSearching ?
          <div className='container text-center vh-100 p-4 d-flex align-items-center justify-content-center'>
            <div className='truffle-loading' role='status' aria-live='polite'>
              <img className='truffle-loading__image' src={require('./Assets/pig_eating_md_clr.gif')} alt="Archive search in progress"/>
              <p className='truffle-loading__eyebrow'>Searching {searchingDomain || 'the archive'}</p>
              <h5 className='truffle-loading__title'>{searchStep.label}</h5>
              <p className='truffle-loading__detail'>{searchProgressState.detail}</p>
              {
                searchProgressState.candidate ?
                  <p className='truffle-loading__meta'>Candidate: {searchProgressState.candidate}</p> : <></>
              }
              <div
                className='truffle-loading__bar'
                aria-label='Search progress'
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={searchProgress}
                role='progressbar'
              >
                <div className='truffle-loading__bar-fill' style={{ width: `${searchProgress}%` }} />
              </div>
              <ol className='truffle-loading__steps'>
                {SEARCH_PROGRESS_STEPS.map((step, index) => (
                  <li
                    key={step.label}
                    className={index <= searchStepIndex ? 'truffle-loading__step truffle-loading__step--active' : 'truffle-loading__step'}
                  >
                    {step.label}
                  </li>
                ))}
              </ol>
            </div>
          </div> :
          hasPreview && searchResult && activeSnapshot ?
            <div className={`truffle-workspace ${isTopNavHidden ? 'truffle-workspace--nav-hidden' : ''}`}>
              {
                !isTopNavHidden ?
                  <header className='truffle-topbar'>
                    <div className='truffle-topbar__row'>
                      <div className='truffle-topbar__brand'>
                        <button
                          type='button'
                          className='truffle-topbar__logo-button'
                          onClick={clearPreview}
                          aria-label='Close preview'
                          title='Close preview'
                        >
                          {
                            isThemeDark ?
                              <img className='truffle-topbar__logo' src={require('./Assets/truffle_logo_light.png')} alt="truffle logo"/> :
                              <img className='truffle-topbar__logo' src={require('./Assets/truffle_logo.png')} alt="truffle logo"/>
                          }
                        </button>
                        <div className='truffle-topbar__brand-copy'>
                          <p className='m-0'><strong>Previewing {searchResult.searchedDomain || selectedDomain || companyQuery}</strong></p>
                          <p className='m-0'><small>Closest archived homepage near {searchResult.requestedDateLabel || formatArchiveDateLabel(dateParts)}. Open the full page if the embed is cramped or broken.</small></p>
                        </div>
                      </div>

                      <div className='truffle-topbar__actions'>
                        <a
                          className='btn btn-sm btn-outline-secondary'
                          href={activeSnapshot.replayUrl}
                          target='_blank'
                          rel='noreferrer'
                        >
                          Open Full Page
                        </a>
                        <button
                          type='button'
                          className='btn btn-sm btn-outline-secondary'
                          onClick={() => setIsTopNavHidden(true)}
                          aria-label='Hide top navigation'
                          title='Hide top navigation'
                        >
                          ↑
                        </button>
                        <button type='button' className='btn btn-sm btn-outline-secondary' onClick={()=>{onDarkThemeButton()}}>
                          ☼
                        </button>
                      </div>
                    </div>

                    <div className='truffle-topbar__row truffle-topbar__row--controls'>
                      <form className='truffle-topbar__form' onSubmit={getWebsite}>
                        <input
                          aria-label='Company or domain'
                          className='truffle-topbar__input'
                          value={companyQuery}
                          onChange={companyChangeHandler}
                          placeholder={"Name or Domain"}
                        ></input>
                        <button type='submit'>Dig!</button>
                      </form>
                      <div className='truffle-topbar__dates'>
                        <select value={dateParts.month} onChange={dateChangeHandler('month')} aria-label='Select month'>
                          {months.map((month) => (
                            <option key={month.value} value={month.value}>{month.label}</option>
                          ))}
                        </select>
                        <select value={dateParts.day} onChange={dateChangeHandler('day')} aria-label='Select day'>
                          {days.map((day) => (
                            <option key={day} value={day}>{day}</option>
                          ))}
                        </select>
                        <select value={dateParts.year} onChange={dateChangeHandler('year')} aria-label='Select year'>
                          {years.map((year) => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {
                      isLoadingCompanies || suggestions.length > 0 ?
                        <div className='truffle-topbar__suggestions'>
                          {isLoadingCompanies && <small>Finding companies...</small>}
                          <div className='truffle-topbar__suggestion-list'>
                            {suggestions.map((company) => (
                              <button
                                key={company.domain}
                                type='button'
                                onClick={() => onSuggestionClick(company.domain)}
                              >
                                {company.name} ({company.domain})
                              </button>
                            ))}
                          </div>
                        </div> : <></>
                    }

                    {
                      error ? <p className='truffle-topbar__error'>{error}</p> : <></>
                    }
                  </header> : <></>
              }

              <section className={`truffle-preview truffle-preview--workspace ${isTopNavHidden ? 'truffle-preview--nav-hidden' : ''} text-start`}>
                <div className='truffle-preview__frame-shell'>
                  {
                    isTopNavHidden ?
                      <button
                        type='button'
                        className='truffle-preview__show-nav'
                        onClick={() => setIsTopNavHidden(false)}
                        aria-label='Show top navigation'
                        title='Show top navigation'
                      >
                        ↓
                      </button> : <></>
                  }
                  {
                    isIframeLoading ?
                      <div className='truffle-preview__loader' role='status' aria-live='polite'>
                        Loading archived page...
                      </div> : <></>
                  }
                  <iframe
                    key={activeSnapshot.replayUrl}
                    className='truffle-preview__frame'
                    src={activeSnapshot.replayUrl}
                    title={`Archived preview of ${searchResult.searchedDomain || activeSnapshot.candidate}`}
                    referrerPolicy='no-referrer'
                    sandbox='allow-same-origin allow-scripts allow-forms allow-popups allow-modals'
                    onLoad={() => setIsIframeLoading(false)}
                  />
                </div>
              </section>
            </div> :
            <div className='container p-4 d-flex flex-column align-items-center justify-content-center vh-100'>
              <div className='w-100 truffle-shell'>
                <div className='text-center'>
                  {
                    isThemeDark ? <img src={require('./Assets/truffle_logo_light.png')} alt="truffle logo"/> : <img src={require('./Assets/truffle_logo.png')} alt="truffle logo"/>
                  }
                  <p>Dig the past of your favorite websites...</p>
                  <form onSubmit={getWebsite}>
                    <input
                      aria-label='Company or domain'
                      value={companyQuery}
                      onChange={companyChangeHandler}
                      placeholder={"Name or Domain"}
                    ></input>
                    <button type='submit' className='mx-2'>Dig!</button>
                  </form>
                  <div className='my-2'>
                    {isLoadingCompanies && <small>Finding companies...</small>}
                    <div
                      className='d-flex flex-wrap justify-content-center gap-2 mx-auto'
                      style={{ maxWidth: '520px' }}
                    >
                      {suggestions.map((company) => (
                        <button
                          key={company.domain}
                          type='button'
                          onClick={() => onSuggestionClick(company.domain)}
                        >
                          {company.name} ({company.domain})
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className='d-flex justify-content-center gap-2 my-2'>
                    <select value={dateParts.month} onChange={dateChangeHandler('month')} aria-label='Select month'>
                      {months.map((month) => (
                        <option key={month.value} value={month.value}>{month.label}</option>
                      ))}
                    </select>
                    <select value={dateParts.day} onChange={dateChangeHandler('day')} aria-label='Select day'>
                      {days.map((day) => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </select>
                    <select value={dateParts.year} onChange={dateChangeHandler('year')} aria-label='Select year'>
                      {years.map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                  <p><small style={{color: "green"}}>Note: Pick a company to auto-fill its domain, or type a full domain and we&apos;ll fetch the closest archived homepage near your date.</small></p>
                  <button type='button' onClick={()=>{onDarkThemeButton()}}>☼</button>
                  {
                    error ? <p style={{ color: 'red' }}>{error}</p> : <></>
                  }
                </div>
                <hr></hr>
                <div className='d-flex'>
                  <div>
                    <img className='img-fluid' src={require('./Assets/bllage.gif')} alt="B Llage logo continuously spinning"/>
                  </div>
                  <div className='text-start'>
                    <p className='mt-2 mb-0'>©2026 B-Llage Digital Media</p>
                    <p className='m-0'><small>This project is possible thanks to the <a href='https://archive.org/web/'>Wayback Machine</a> and the <a href='https://archive.org/'>Internet Archive</a>.</small></p>
                    <p><small>See more projects by me: <a href='https://hectormagana.art'>Héctor Magaña</a></small></p>
                  </div>
                  <img src={require('./Assets/pigflying.gif')} alt="Pigs flying with the word 'home' attached"/>
                </div>
              </div>
            </div>
      }
    </div>
  );
}

export default App;
